import { watch, toValue } from 'vue';
import { onKeyStroke, createSharedComposable, tryOnScopeDispose } from '@vueuse/core';

/**
 * Escape dismisses only the most recently opened layer.
 *
 * Each caller registers while `active` is truthy; a single window listener hands
 * the key to the top of the stack and nobody else. Without it every open layer
 * - modal, expanded image, navbar menu - closed on the same keypress.
 *
 * A layer that is active but refuses to close (e.g. NModal with `preventClose`)
 * still consumes the key, so the layer underneath stays put.
 *
 * radix-vue layers (ui/dropdown-menu) close themselves on Escape through their
 * own document listener; while one is open it is the top layer, so the stack
 * stands down instead of closing whatever sits underneath as well.
 */
const stack = [];

// One listener shared by every caller, attached with the first and removed with
// the last. `dedupe` drops auto-repeat from a held key.
const useEscapeListener = createSharedComposable(() => {
    onKeyStroke('Escape', (e) => {
        if (e.isComposing || document.querySelector('[data-dismissable-layer]')) {
            return;
        }

        stack.at(-1)?.(e);
    }, { dedupe: true });
});

export function useEscapeStack(active, handler) {
    const entry = (e) => handler(e);

    const remove = () => {
        const index = stack.indexOf(entry);

        if (index !== -1) {
            stack.splice(index, 1);
        }
    };

    useEscapeListener();

    // Re-activating moves the layer to the top: it is the one the user just opened.
    watch(() => !!toValue(active), (isActive) => {
        remove();

        if (isActive) {
            stack.push(entry);
        }
    }, { immediate: true });

    tryOnScopeDispose(remove);
}
